/* SemBot · formattazione dei numeri.
 *
 * Unica fonte di verità: nessun componente scrive separatori di migliaia a
 * mano. In it-IT il raggruppamento predefinito NON si applica ai numeri da
 * 1000 a 9999, quindi lo stesso importo comparirebbe come "3300" in una riga e
 * "13.240" in quella accanto: `useGrouping: 'always'` è quello che lo evita.
 */

const LOCALE = 'it-IT';

/** Numero con separatore di migliaia e decimali fissi. */
export function num(n: number, decimali = 0): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(LOCALE, {
    useGrouping: 'always',
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
  });
}

/** Percentuale col segno esplicito. Il meno è U+2212, non il trattino. */
export function pct(n: number, decimali = 0): string {
  if (!Number.isFinite(n)) return '—';
  return (n >= 0 ? '+' : '−') + num(Math.abs(n), decimali) + '%';
}

/** Drawdown: sempre negativo a video, anche se il dato arriva positivo. */
export function drawdown(n: number, decimali = 1): string {
  if (!Number.isFinite(n)) return '—';
  return '−' + num(Math.abs(n), decimali) + '%';
}

/** Importo in valuta. Il simbolo precede, come nel prototipo. */
export function valuta(n: number, simbolo = '$', decimali = 0): string {
  if (!Number.isFinite(n)) return '—';
  const segno = n < 0 ? '−' : '';
  return segno + simbolo + num(Math.abs(n), decimali);
}

/** Importo firmato: usato per il non realizzato, dove il segno è il messaggio. */
export function valutaFirmata(n: number, simbolo = '$', decimali = 0): string {
  if (!Number.isFinite(n)) return '—';
  return (n >= 0 ? '+' : '−') + simbolo + num(Math.abs(n), decimali);
}

/**
 * Etichetta del periodo di misura, es. "2017–2026".
 * STATO.md regola 2: nessun numero compare senza il periodo su cui è misurato,
 * perché le stesse strategie danno esiti opposti su 2017–2026 e su 2022–2026.
 */
/**
 * Il periodo misurato, con le date PER ESTESO.
 *
 * Prima mostrava i soli anni -- «2017–2026» -- ed era troppo poco: due backtest che leggono
 * entrambi «2017–2026» possono partire a tre mesi di distanza, e su un rendimento composto tre
 * mesi di bull market cambiano il totale di parecchio. Il periodo e' il metro con cui si legge
 * ogni numero della riga, e un metro approssimato falsa il confronto proprio fra righe che
 * sembrano confrontabili.
 */
export function periodo(dal?: string | Date | null, al?: string | Date | null): string {
  const a = giorno(dal);
  const b = giorno(al);
  if (a == null && b == null) return '—';
  if (a == null) return `fino al ${b}`;
  if (b == null) return `dal ${a}`;
  return `${a} – ${b}`;
}

/** Data breve ma completa: «17 ago 2017». Il mese a parole evita l'ambiguita' fra 03/04 e 04/03. */
function giorno(d?: string | Date | null): string | null {
  if (d == null) return null;
  const data = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}


/** Durata di un periodo in forma breve, es. "9a 4m". */
export function durataPeriodo(dal?: string | Date | null, al?: string | Date | null): string {
  if (!dal || !al) return '';
  const a = dal instanceof Date ? dal : new Date(dal);
  const b = al instanceof Date ? al : new Date(al);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  const mesi = Math.max(0, Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
  const anni = Math.floor(mesi / 12);
  const resto = mesi % 12;
  if (anni === 0) return `${resto}m`;
  if (resto === 0) return `${anni}a`;
  return `${anni}a ${resto}m`;
}

/** Timeframe in minuti → etichetta breve (15m, 1h, 4h, 1d). */
export function timeframe(minuti?: number | null): string {
  if (minuti == null || !Number.isFinite(minuti)) return '—';
  if (minuti < 60) return `${minuti}m`;
  if (minuti < 1440) return `${minuti / 60}h`;
  return `${minuti / 1440}d`;
}

/** Ora di lettura di un dato vivo, es. "14:33:12". */
export function ora(d: string | Date | number): string {
  const data = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleTimeString(LOCALE, { hour12: false });
}

/** Data e ora complete, per le tabelle. */
export function dataOra(d: string | Date | number): string {
  const data = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Da quanto tempo, in italiano: "ora", "4 minuti fa", "2g 4h".
 * Fuori casa la rete è incerta: un numero senza l'età della lettura non è un
 * numero su cui si può decidere.
 */
export function daQuanto(d: string | Date | number, adesso = Date.now()): string {
  const data = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(data.getTime())) return '—';
  const sec = Math.max(0, Math.round((adesso - data.getTime()) / 1000));
  if (sec < 45) return 'ora';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} ${min === 1 ? 'minuto' : 'minuti'} fa`;
  const ore = Math.floor(min / 60);
  if (ore < 24) return `${ore}h ${min % 60}m fa`;
  const giorni = Math.floor(ore / 24);
  return `${giorni}g ${ore % 24}h fa`;
}
